import React from "react";
import { func, node, oneOf, oneOfType, string } from "prop-types";

import buttonThemes from "../../constants/buttonThemes";
import calloutCardThemes from "../../constants/calloutCardThemes";
import imagePositions from "../../constants/imagePositions";
import * as S from "../styled";

const VerticalCalloutCardButton = React.forwardRef(
  (
    {
      buttonTheme,
      callToActionCopy,
      description,
      headline,
      illustration,
      imagePosition,
      legalCopy,
      theme,
      ...passThroughProps
    },
    ref
  ) => (
    <S.VerticalCardWrapper
      ref={ref}
      as="button"
      interactive
      theme={theme}
      {...passThroughProps}
    >
      {illustration && (
        <S.IllustrationWrapper imagePosition={imagePosition}>
          {illustration}
        </S.IllustrationWrapper>
      )}
      <S.ContentWrapper>
        <S.Headline>{headline}</S.Headline>
        {description && <S.Description>{description}</S.Description>}
      </S.ContentWrapper>
      <S.PseudoButton buttonTheme={buttonTheme} theme={theme}>
        {callToActionCopy}
      </S.PseudoButton>
      {legalCopy && <S.LegalCopy>{legalCopy}</S.LegalCopy>}
    </S.VerticalCardWrapper>
  )
);

VerticalCalloutCardButton.propTypes = {
  /**
   * The theme for the pseudo button within the Callout Card. One of:
   *   - PRIMARY_BUTTON
   *   - SECONDARY_BUTTON
   */
  buttonTheme: oneOf(Object.values(buttonThemes)),

  /**
   * The text inside of the pseudo button within the Callout Card
   */
  callToActionCopy: oneOfType([node, string]).isRequired,

  /**
   * Adds a description block to the Callout Card
   */
  description: string,

  /**
   * Headline for the Callout Card
   */
  headline: string.isRequired,

  /**
   * A component that defines the illustration for the Callout Card.
   */
  illustration: node,

  /**
   * If an illustration is provided, imagePosition will allow to align it to the bottom.
   */
  imagePosition: oneOf([imagePositions.TOP, imagePositions.BOTTOM]),

  /**
   * Adds a legal copy block to the Callout Card
   */
  legalCopy: oneOfType([node, string]),

  /**
   * Click callback handler when a user clicks the Callout Card
   * (the entire card, not just the link)
   */
  onClick: func,

  /**
   * The Callout Card theme. One of:
   *   - BLUE
   *   - ORANGE
   *   - GRAY
   *   - WHITE
   */
  theme: oneOf(Object.values(calloutCardThemes))
};

VerticalCalloutCardButton.defaultProps = {
  buttonTheme: buttonThemes.PRIMARY_BUTTON,
  description: null,
  legalCopy: null,
  illustration: null,
  imagePosition: imagePositions.TOP,
  onClick: null,
  theme: calloutCardThemes.WHITE
};

VerticalCalloutCardButton.displayName = "VerticalCalloutCardButton";

export default VerticalCalloutCardButton;
